'use client'
import { cn } from '@/lib/utils'
import { scrollToProductSection } from '@/lib/utils/scroll-utils'
import { TCardFacet, TCollectionData, TFacet } from '@/types/collection'
import { useTranslations } from 'next-intl'
import { useShallow } from 'zustand/react/shallow'
import { shouldHideSorting } from '../../../constants'
import { useCollectionData } from '../../../hooks/use-collection-data'
import { useCollectionFilterStoreV2 } from '../../../store/collection-filter-store-v2'
import {
  FilterTag,
  getFilterTagsFromUrl,
} from '../../../utils/filter-tag-utils'
import { FilterBarTags } from '../filter-bar-tags'
import { FilterSortBy } from '../filter-sortby'

type Props = {
  showTitle?: boolean
  collectionTitle?: string
  facets: TFacet[]
  card_facets: TCardFacet[]
  serverData: TCollectionData
  initialFilters?: Record<string, string | string[] | number | undefined>
  startSlot?: React.ReactNode
}

export const FilterSummaryBar = ({
  showTitle,
  collectionTitle,
  facets,
  card_facets,
  serverData,
  initialFilters,
  startSlot,
}: Props) => {
  const t = useTranslations('collection_page')

  // Subscribe vào collection data để tự động cập nhật title khi filter thay đổi
  const { data } = useCollectionData({
    serverData: serverData || ({} as TCollectionData),
    initialFilters: initialFilters || {},
  })

  // Get title từ latest data nếu có serverData, fallback về collectionTitle prop
  // Priority: latest data > serverData > collectionTitle prop
  const displayTitle =
    serverData && initialFilters
      ? (data?.pages?.[0]?.collection_detail?.title_display ??
        serverData?.collection_detail?.title_display ??
        collectionTitle)
      : collectionTitle

  const { filters, clearFilters, setFilter, setConcatValue } =
    useCollectionFilterStoreV2(
      useShallow((state) => ({
        filters: state.filters,
        clearFilters: state.clearFilters,
        setFilter: state.setFilter,
        setConcatValue: state.setConcatValue,
      }))
    )

  const onRemoveTag = (tag: FilterTag) => {
    const currentValue = filters[tag.parentKey]

    if (Array.isArray(currentValue)) {
      const newValues = currentValue.filter((v) => v !== tag.value)
      setFilter(tag.parentKey, newValues)
    } else {
      // Kiểm tra xem filter này có phải là concat slug không
      const facet = facets.find((f) => f.seo_alias === tag.parentKey)
      const cardFacet = card_facets.find((f) => f.seo_alias === tag.parentKey)
      if (facet?.is_concat_slug || cardFacet?.is_concat_slug) {
        // Nếu là concat slug, cần clear concatValue
        setConcatValue(null)
      }
      setFilter(tag.parentKey, undefined)
    }

    // Cuộn lên đầu danh sách sản phẩm sau khi xóa filter tag
    scrollToProductSection(200, 50)
  }

  const handleClearFilters = () => {
    clearFilters()

    // Cuộn lên đầu danh sách sản phẩm sau khi xóa toàn bộ filter
    scrollToProductSection(200, 50)
  }

  // Get tags directly from URL instead of store
  const currentUrl =
    typeof window !== 'undefined' ? new URL(window.location.href) : null
  const originalSlug = useCollectionFilterStoreV2((state) => state.originalSlug)
  const tags =
    currentUrl && originalSlug
      ? getFilterTagsFromUrl(currentUrl, originalSlug, facets, card_facets)
      : []
  const hasFilter = tags.length > 0
  const hideSorting = shouldHideSorting(originalSlug)

  if (!facets && !card_facets) {
    return null
  }

  return (
    <>
      {showTitle && hasFilter && displayTitle && (
        <h1 className='mt-6 font-criteria text-3xl font-medium uppercase leading-7 text-neutral-900'>
          {displayTitle}
        </h1>
      )}
      <div className={cn('flex justify-between', !startSlot && 'items-center')}>
        <div
          className={cn(
            'flex basis-8/12 gap-3',
            startSlot ? 'flex-col-reverse' : 'items-center'
          )}
        >
          {startSlot}
          {showTitle && !hasFilter && displayTitle && (
            <h1 className='font-criteria text-3xl font-medium leading-7 text-neutral-900'>
              {displayTitle}
            </h1>
          )}
          <FilterBarTags tags={tags} onRemoveTag={onRemoveTag} />
          {hasFilter && (
            <button
              className='whitespace-nowrap font-sans text-sm font-semibold leading-4.5 text-primary underline-offset-2 hover:underline'
              onClick={handleClearFilters}
            >
              {t('button.clear_filter')}
            </button>
          )}
        </div>
        {!hideSorting && (
          <FilterSortBy
            serverData={serverData}
            initialFilters={initialFilters}
          />
        )}
      </div>
    </>
  )
}
